import { ResponsiveSelect } from "@components/responsive-select";
import UpgradeNeeded from "@components/upgrade-needed";
import { LorasType, LorasTypeDetails } from "@features/loras/type";
import { useLoras } from "@features/loras/use-loras";
import { ArrowRightIcon, SparklesIcon } from "@heroicons/react/24/solid";
import {
  Blockquote,
  Box,
  Button,
  Checkbox,
  Heading,
  IconButton,
  Link,
  Tabs,
  Text,
  TextArea,
  TextField,
  Tooltip,
} from "@radix-ui/themes";
import { motion } from "framer-motion";
import { useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { twMerge } from "tailwind-merge";

// Define field options based on type.ts
const FIELD_OPTIONS: Partial<
  Record<keyof LorasTypeDetails, readonly string[]>
> = {
  gender: ["man", "woman", "androgynous"] as const,
  body_hair: ["none", "light", "medium", "heavy", "very heavy"] as const,
  chest: ["flat", "small", "medium", "large", "very large"] as const,
  body: [
    "large",
    "chubby",
    "lean",
    "medium",
    "slim",
    "muscular",
    "slight",
  ] as const,
  height: ["tall", "short", "average"] as const,
  skin: [
    "pale",
    "light",
    "olive",
    "tan",
    "brown",
    "black",
    "blue",
    "red",
    "orange",
    "green",
    "yellow",
    "purple",
    "multicolor",
  ] as const,
  ratio: [
    "square_hd",
    "square",
    "portrait_4_3",
    "portrait_16_9",
    "landscape_4_3",
    "landscape_16_9",
  ] as const,
};

export const LoraEdit = (props: {
  lora: Pick<LorasType, "status" | "type" | "subtype" | "ai" | "name">;
  value: Pick<
    LorasType,
    "details" | "public" | "friends" | "allow_nsfw_from_other"
  >;
  onChange: (
    value: Pick<
      LorasType,
      "details" | "public" | "friends" | "allow_nsfw_from_other"
    >
  ) => void;
  onCancel?: () => void;
}) => {
  const { t } = useTranslation();
  const [draft, setDraft] = useState(props.value);

  return (
    <div className="flex w-full h-full">
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.5 }}
        className="h-max mx-auto md:my-auto grow w-full mobile-safe-bottom"
      >
        {props.lora.status === "completed" && (
          <Heading>{t("loras.edit.title_completed")}</Heading>
        )}
        {props.lora.status !== "completed" && (
          <Heading>{t("loras.edit.title_in_progress")}</Heading>
        )}
        <Text>
          {props.lora.status !== "completed" &&
            t("loras.edit.description_in_progress")}
          {props.lora.status === "completed" &&
            t("loras.edit.description_completed")}
        </Text>

        <form
          onSubmit={(e) => {
            e.preventDefault();
            e.stopPropagation();
            props.onChange(draft);
          }}
        >
          <Box className="mt-4 space-y-2">
            <Heading size="2">{t("loras.edit.name")}</Heading>
            <TextField.Root
              placeholder={t("loras.edit.name")}
              size="3"
              value={props.lora.name}
              disabled
            />
          </Box>

          <LoraEditForm
            {...props}
            draft={draft}
            setDraft={setDraft}
            onDelete={() => props.onCancel?.()}
          />

          {props.onCancel && (
            <Button
              type="button"
              variant="outline"
              color="gray"
              size="2"
              className={twMerge("mt-8 float-left")}
              onClick={() => props.onCancel?.()}
            >
              {t("loras.edit.cancel")}
            </Button>
          )}

          <Button
            type="submit"
            size="2"
            className={twMerge("mt-8 float-right")}
          >
            <Trans
              i18nKey="loras.edit.save"
              values={{ name: props.lora.name }}
              components={[<span key={"0"}></span>]}
            />
            <ArrowRightIcon className="h-4 w-4" />
          </Button>
        </form>
      </motion.div>
    </div>
  );
};

export const LoraEditForm = ({
  draft,
  setDraft,
  ...props
}: {
  lora: Pick<LorasType, "status" | "type" | "subtype" | "ai" | "name"> & {
    url?: string;
    id?: string;
  };
  draft: Pick<
    LorasType,
    "details" | "public" | "friends" | "allow_nsfw_from_other"
  >;
  setDraft: (
    value: Pick<
      LorasType,
      "details" | "public" | "friends" | "allow_nsfw_from_other"
    >
  ) => void;
  onDelete?: () => void;
}) => {
  const { t } = useTranslation();
  const { remove } = useLoras();

  // Compute visibility from draft properties
  const getVisibility = (): "public" | "private" | "friends" => {
    if (draft.public) return "public";
    if (draft.friends) return "friends";
    return "private";
  };

  return (
    <>
      <PrefillableBox
        onChange={setDraft}
        value={draft}
        lora={props.lora}
        ky="description"
        title={t("loras.edit.description")}
        type="area"
      />

      <Tabs.Root className="mt-4" defaultValue="general">
        <Tabs.List>
          <Tabs.Trigger value="general" className="grow">
            {t("loras.edit.tabs.general")}
          </Tabs.Trigger>
          {props.lora.subtype === "avatar" && (
            <>
              <Tabs.Trigger value="face" className="grow">
                {t("loras.edit.tabs.face")}
              </Tabs.Trigger>
              <Tabs.Trigger value="body" className="grow">
                {t("loras.edit.tabs.body")}
              </Tabs.Trigger>
            </>
          )}
          {props.lora.id && (
            <Tabs.Trigger value="more" className="grow">
              {t("loras.edit.tabs.more")}
            </Tabs.Trigger>
          )}
        </Tabs.List>

        {props.lora.subtype === "avatar" && (
          <>
            <Tabs.Content value="general">
              <PrefillableBox
                onChange={setDraft}
                value={draft}
                lora={props.lora}
                ky="gender"
                title={t("loras.edit.gender")}
              />
              <PrefillableBox
                onChange={setDraft}
                value={draft}
                lora={props.lora}
                ky="age"
                title={t("loras.edit.age")}
              />
              <PrefillableBox
                onChange={setDraft}
                value={draft}
                lora={props.lora}
                ky="skin"
                title={t("loras.edit.skin")}
              />
            </Tabs.Content>
            <Tabs.Content value="face">
              <PrefillableBox
                onChange={setDraft}
                value={draft}
                lora={props.lora}
                ky="face"
                title={t("loras.edit.face")}
                type="area"
              />
              <PrefillableBox
                onChange={setDraft}
                value={draft}
                lora={props.lora}
                ky="hair"
                title={t("loras.edit.hair")}
              />
              <PrefillableBox
                onChange={setDraft}
                value={draft}
                lora={props.lora}
                ky="hair_color"
                title={t("loras.edit.hair_color")}
              />
            </Tabs.Content>
            <Tabs.Content value="body">
              <PrefillableBox
                onChange={setDraft}
                value={draft}
                lora={props.lora}
                ky="height"
                title={t("loras.edit.height")}
              />
              <PrefillableBox
                onChange={setDraft}
                value={draft}
                lora={props.lora}
                ky="body"
                title={t("loras.edit.body")}
              />
              <PrefillableBox
                onChange={setDraft}
                value={draft}
                lora={props.lora}
                ky="chest"
                title={t("loras.edit.chest")}
              />
              <PrefillableBox
                onChange={setDraft}
                value={draft}
                lora={props.lora}
                ky="body_hair"
                title={t("loras.edit.body_hair")}
              />
            </Tabs.Content>
          </>
        )}

        {props.lora.subtype === "animal" && (
          <Tabs.Content value="general">
            <PrefillableBox
              onChange={setDraft}
              value={draft}
              lora={props.lora}
              ky="species"
              title={t("loras.edit.species")}
            />
            <PrefillableBox
              onChange={setDraft}
              value={draft}
              lora={props.lora}
              ky="breed"
              title={t("loras.edit.breed")}
            />
            <PrefillableBox
              onChange={setDraft}
              value={draft}
              lora={props.lora}
              ky="color"
              title={t("loras.edit.color")}
            />
            <PrefillableBox
              onChange={setDraft}
              value={draft}
              lora={props.lora}
              ky="size"
              title={t("loras.edit.size")}
            />
          </Tabs.Content>
        )}

        {props.lora.subtype === "car" && (
          <Tabs.Content value="general">
            <PrefillableBox
              onChange={setDraft}
              value={draft}
              lora={props.lora}
              ky="brand"
              title={t("loras.edit.brand")}
            />
            <PrefillableBox
              onChange={setDraft}
              value={draft}
              lora={props.lora}
              ky="category"
              title={t("loras.edit.category")}
            />
            <PrefillableBox
              onChange={setDraft}
              value={draft}
              lora={props.lora}
              ky="color"
              title={t("loras.edit.color")}
            />
          </Tabs.Content>
        )}

        {props.lora.subtype === "object" && (
          <Tabs.Content value="general">
            <PrefillableBox
              onChange={setDraft}
              value={draft}
              lora={props.lora}
              ky="category"
              title={t("loras.edit.category")}
            />
            <PrefillableBox
              onChange={setDraft}
              value={draft}
              lora={props.lora}
              ky="brand"
              title={t("loras.edit.brand")}
            />
            <PrefillableBox
              onChange={setDraft}
              value={draft}
              lora={props.lora}
              ky="color"
              title={t("loras.edit.color")}
            />
            <PrefillableBox
              onChange={setDraft}
              value={draft}
              lora={props.lora}
              ky="size"
              title={t("loras.edit.size")}
            />
          </Tabs.Content>
        )}

        <Tabs.Content value="more">
          <Box className="mt-4 space-y-2">
            <Heading size="2">{t("loras.edit.visibility.title")}</Heading>
            <ResponsiveSelect
              size="3"
              value={getVisibility()}
              onChange={(value) => {
                const visibility = value as "public" | "private" | "friends";
                setDraft({
                  ...draft,
                  public: visibility === "public",
                  friends: visibility === "friends",
                });
              }}
              items={[
                {
                  value: "public",
                  label: t("loras.edit.visibility.public"),
                },
                {
                  value: "private",
                  label: t("loras.edit.visibility.private"),
                },
                ...(false
                  ? [
                      {
                        value: "friends",
                        label: t("loras.edit.visibility.friends"),
                      },
                    ]
                  : []),
              ]}
              placeholder={t(`loras.edit.visibility.${getVisibility()}`)}
              renderTrigger={(item) =>
                t(`loras.edit.visibility.${getVisibility()}`)
              }
            />
            <Blockquote size="2">
              {t(`loras.edit.visibility.${getVisibility()}_description`)}
            </Blockquote>
          </Box>

          <Box className="mt-4 space-y-2">
            <div className="flex items-center space-x-2 mt-4">
              <Checkbox
                size="3"
                checked={draft.allow_nsfw_from_other}
                onCheckedChange={(checked) =>
                  setDraft({
                    ...draft,
                    allow_nsfw_from_other: !!checked,
                  })
                }
              />
              <Text size="2">{t("loras.edit.adult_content")}</Text>
            </div>
            <Blockquote size="2">
              {t("loras.edit.adult_content_description")}
            </Blockquote>
          </Box>

          <Box className="mt-8 space-y-2">
            <Heading size="2">{t("loras.edit.delete")}</Heading>
            <Button
              variant="solid"
              color="red"
              size="1"
              onClick={() => {
                if (window.confirm(t("loras.edit.delete_confirmation"))) {
                  remove(props.lora.id as string);
                  props.onDelete?.();
                }
              }}
            >
              {t("loras.edit.delete_button")}
            </Button>
          </Box>
          {!!props.lora.url && (
            <Box className="mt-4 space-y-2">
              <Heading size="2">{t("loras.edit.export")}</Heading>
              <UpgradeNeeded minimumPlan="business">
                <Link
                  href={props.lora.url}
                  target="_blank"
                  rel="noreferrer"
                  className="block"
                >
                  {t("loras.edit.download_model")}
                </Link>
              </UpgradeNeeded>
            </Box>
          )}
        </Tabs.Content>
      </Tabs.Root>
    </>
  );
};

const PrefillableBox = (props: {
  lora: Pick<LorasType, "ai">;
  value: Pick<
    LorasType,
    "details" | "public" | "friends" | "allow_nsfw_from_other"
  >;
  onChange: (
    value: Pick<
      LorasType,
      "details" | "public" | "friends" | "allow_nsfw_from_other"
    >
  ) => void;
  ky: keyof LorasType["details"];
  title: string;
  type?: "area";
}) => {
  const { t } = useTranslation();
  const fieldOptions = FIELD_OPTIONS[props.ky];
  const currentValue = (props.value.details as any)?.[props.ky];

  // Helper to get translated value
  const getTranslatedValue = (value: string | number) => {
    return t([`loras.details.${props.ky}.${value}`, String(value)]);
  };

  return (
    <Box className="mt-4 space-y-2">
      {(props.lora.ai as any)?.[props.ky] && (
        <Tooltip
          content={t("loras.edit.use_default_value", {
            value: getTranslatedValue((props.lora.ai as any)?.[props.ky]),
          })}
        >
          <IconButton
            type="button"
            size="1"
            variant="ghost"
            className="float-right"
            onClick={() => {
              props.onChange({
                ...props.value,
                details: {
                  ...props.value.details,
                  [props.ky]: (props.lora.ai as any)?.[props.ky] as any,
                },
              });
            }}
          >
            <SparklesIcon className="h-4 w-4" />
          </IconButton>
        </Tooltip>
      )}
      <Heading size="2">{props.title}</Heading>

      {/* Use select for predefined options */}
      {fieldOptions && !props.type ? (
        <ResponsiveSelect
          size="3"
          value={currentValue || ""}
          onChange={(value) => {
            props.onChange({
              ...props.value,
              details: {
                ...props.value.details,
                [props.ky]: value,
              },
            });
          }}
          items={fieldOptions.map((option) => ({
            value: option,
            label: getTranslatedValue(option),
          }))}
          placeholder={props.title}
          renderTrigger={(item) =>
            item ? getTranslatedValue(item.value) : props.title
          }
        />
      ) : props.type === "area" ? (
        <TextArea
          placeholder={props.title}
          size="3"
          value={currentValue as string}
          onChange={(e) =>
            props.onChange({
              ...props.value,
              details: {
                ...props.value.details,
                [props.ky]: e.target.value,
              },
            })
          }
        />
      ) : (
        <TextField.Root
          placeholder={props.title}
          size="3"
          value={currentValue as string}
          onChange={(e) =>
            props.onChange({
              ...props.value,
              details: {
                ...props.value.details,
                [props.ky]: e.target.value,
              },
            })
          }
        />
      )}
    </Box>
  );
};
